
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface RequireTPUserProps {
  children: React.ReactNode;
}

interface ProfileWithRole {
  role: string;
}

const RequireTPUser = ({ children }: RequireTPUserProps) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isTPUser, setIsTPUser] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkTPUserStatus = async () => {
      try {
        // Important: First check if we're still authenticating
        if (authLoading) {
          console.log("Auth is still loading, waiting before checking permissions...");
          return; // Don't proceed until auth loading is complete
        }

        // Now check if we have a user
        if (!user) {
          console.log("No authenticated user found, redirecting to login");
          navigate("/auth/login", { 
            state: { from: location.pathname }
          });
          return;
        }

        console.log("User authenticated, checking permissions for user:", user.id);

        // Check if user is an admin first
        const { data: isAdmin, error: adminError } = await supabase
          .rpc('is_admin_secure', { input_user_id: user.id });
        
        if (adminError) {
          console.error('Error checking admin status:', adminError);
        }
        
        if (isAdmin) {
          console.log("User is admin, granting access");
          setIsTPUser(true);
          setIsLoading(false);
          return;
        }

        // If not admin, check profile role
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error checking profile role:', profileError);
          navigate("/geentoegang");
          return;
        }

        // Use type assertion to handle the role property
        const hasAccess = (profile as ProfileWithRole)?.role === 'tp_user';
        console.log("User role check:", {
          role: (profile as ProfileWithRole)?.role,
          hasAccess
        });
        setIsTPUser(hasAccess);
        
        if (!hasAccess) {
          navigate("/geentoegang");
        }
      } catch (error) {
        console.error('Error checking access permissions:', error);
        navigate("/geentoegang");
      } finally {
        setIsLoading(false);
      }
    };

    checkTPUserStatus();
  }, [user, navigate, location, authLoading]); // Added authLoading as dependency

  if (authLoading || isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Bezig met laden...</p>
        </div>
      </div>
    );
  }

  return isTPUser ? <>{children}</> : null;
};

export default RequireTPUser;
